// desyr ID on this device (36-desyr-id.md): whether "Your circles" stays on screen while circles
// sign in one after the other, and signing out of desyr ID (Behaviour 4): every circle here, then
// the service's end-session address in this window, back to the signed-out page.
import type { Config } from '../core/config';
import { ALL_OF_DESYR, atMost, clearStorageForSignOut, databasesLeft, leaveFor, signOutBy } from '../core/device';
import { readSession } from '../core/session';
import { storePrefix, waiting } from '../accounts/store';
import { random } from '../core/mail';
import { endSessionAddress, idAnswers } from './oidc';
import { markSignedOut, readId } from './store';
import { signOutTabs } from './tabs';

const CARD = 'desyr_id_circles';
/** "Your circles" is open in this tab (from a first sign-in until "Open desyr"). */
export function circlesCard(open?: boolean): boolean {
  try {
    if (open === undefined) return sessionStorage.getItem(CARD) === '1';
    if (open) sessionStorage.setItem(CARD, '1');
    else sessionStorage.removeItem(CARD);
  } catch {
    // storage refused: the card shows only right after the sign-in
  }
  return !!open;
}

/**
 * Where signing out of desyr ID ends: the end-session address when desyr ID answers by `by` (the press's
 * 10 s, asked alongside the sign-out's steps), else `/login`, unconfirmed (v1.58.74: never the browser's
 * error page). Its random `state` is kept in `desyr_signed_out`; only the return with it confirms the end (v1.58.63, 4.3).
 */
export async function endOfId(config: Config, by = signOutBy()): Promise<string> {
  const id = readId();
  if (!config.desyrId || !id || navigator.onLine === false) return '/login';
  const state = random(16);
  markSignedOut(state);
  const [address, answers] = await Promise.all([
    atMost(endSessionAddress(config.desyrId, id, state).catch(() => undefined), Math.max(0, by - Date.now())),
    idAnswers(config.desyrId, by),
  ]);
  return address && answers ? address : '/login';
}

/** "Sign out" from "Your circles" (no circle open yet): every circle on this device, then desyr ID. */
export async function signOutOfId(config: Config): Promise<void> {
  signOutTabs(); // first of all (Behaviour 4.4.1, v1.58.63)
  const by = signOutBy(); // 03, Deadlines (v1.58.74): desyr ID asked meanwhile, the card within 12 s
  const after = endOfId(config, by);
  const { removeWaiting } = await import('../accounts/accounts');
  const current = readSession();
  for (const a of [...waiting(), ...(current ? [{ ...current, db: storePrefix() }] : [])]) await removeWaiting(a, by);
  databasesLeft(ALL_OF_DESYR); // deleted at the next start, before anything opens them
  clearStorageForSignOut();
  leaveFor(await after);
}
